import Dialog from '@mui/material/Dialog';
import React from 'react';
import { superAdminTooltips } from '../../../../shared/config/actionHelpCatalog';
import HelpTooltipTrigger from '../../../../shared/ui/common/HelpTooltipTrigger';
import {
  getDocumentCommandPresentation,
  getDocumentStatePresentation,
  type DocumentUiCommand,
} from '../../lib/legalDocumentPresentation';
import type {
  AdminDocumentPackageDetail,
  LegalPackageCommand,
} from '../../legalTypes';
import { AdminButton } from '../components';

const CapabilityAlias: Record<string, string> = {
  Generate: 'generate',
  generate: 'generate',
  RegenerateBeforeApproval: 'regenerate',
  regenerate_before_approval: 'regenerate',
  regenerate: 'regenerate',
  Approve: 'approve',
  approve: 'approve',
  Amend: 'amend',
  amend: 'amend',
  Void: 'void',
  void: 'void',
  Archive: 'archive',
  archive: 'archive',
  Restore: 'restore',
  restore: 'restore',
  Send: 'send',
  send: 'send',
  Decline: 'decline',
  decline: 'decline',
  resend: 'resend',
  revoke: 'revoke',
  complete: 'complete',
  upload: 'upload',
};

export const documentCapabilityAvailable = (
  detail: AdminDocumentPackageDetail,
  capability: string
): boolean => detail.availableCommands.some((candidate) => CapabilityAlias[candidate] === capability);

interface PendingCommand {
  uiCommand: DocumentUiCommand;
  packageCommand?: LegalPackageCommand;
}

const PackageButtons: ReadonlyArray<{
  ui: DocumentUiCommand;
  command: LegalPackageCommand;
  capability: string;
}> = [
  { ui: 'regenerate_before_approval', command: 'regenerate', capability: 'regenerate' },
  { ui: 'approve', command: 'approve', capability: 'approve' },
  { ui: 'amend', command: 'amend', capability: 'amend' },
  { ui: 'void', command: 'void', capability: 'void' },
  { ui: 'archive', command: 'archive', capability: 'archive' },
  { ui: 'restore', command: 'restore', capability: 'restore' },
  { ui: 'send', command: 'send', capability: 'send' },
  { ui: 'decline', command: 'decline', capability: 'decline' },
];

const RequiresConfirmation: Record<DocumentUiCommand, true | undefined> = {
  complete_missing_input: undefined,
  generate: undefined,
  retry_generation: undefined,
  regenerate_before_approval: undefined,
  approve: undefined,
  amend: true,
  void: true,
  archive: true,
  restore: undefined,
  send: true,
  resend: true,
  revoke: true,
  upload_signed: undefined,
  complete_execution: true,
  decline: true,
};

interface TypedCommandDialogProps {
  pending: PendingCommand | null;
  companyName: string;
  packageVersion: number;
  busy: boolean;
  onConfirm: (command: PendingCommand) => Promise<void>;
  onClose: () => void;
}

const TypedCommandDialog: React.FC<TypedCommandDialogProps> = ({
  pending,
  companyName,
  packageVersion,
  busy,
  onConfirm,
  onClose,
}) => {
  const [confirmation, setConfirmation] = React.useState('');
  React.useEffect(() => setConfirmation(''), [pending]);
  if (!pending) {
    return null;
  }
  const presentation = getDocumentCommandPresentation(pending.uiCommand);
  const required = `${companyName} / v${packageVersion}`;
  const destructive = presentation.destructive;
  const confirmLabel = presentation.confirmLabel ?? presentation.label;
  return (
    <Dialog open aria-labelledby="document-command-confirmation-title" className="pipeex-admin-portal admin-legal-dialog" onClose={() => !busy && onClose()}>
      <form className="admin-legal-dialog__body admin-legal-dialog__body--confirm" onSubmit={(event) => { event.preventDefault(); void onConfirm(pending); }}>
        <header><span className="admin-legal-eyebrow">{destructive ? 'Irreversible legal action' : 'Legal lifecycle action'}</span><h2 id="document-command-confirmation-title">Confirm {presentation.label.toLocaleLowerCase()}</h2></header>
        <p>{presentation.effect}</p>
        <dl className="admin-legal-detail-grid"><div><dt>Customer</dt><dd>{companyName}</dd></div><div><dt>Package</dt><dd>Package v{packageVersion}</dd></div></dl>
        {destructive && <><label>Type package confirmation<input autoFocus value={confirmation} onChange={(event) => setConfirmation(event.target.value)} placeholder={required} /></label><p className="admin-legal-confirm-hint">Type <strong>{required}</strong> to confirm this effect.</p></>}
        <footer className="admin-legal-dialog__actions"><AdminButton variant="ghost" disabled={busy} onClick={onClose}>Cancel</AdminButton><AdminButton type="submit" variant={destructive ? 'danger' : 'primary'} disabled={busy || (destructive && confirmation !== required)}>{confirmLabel}</AdminButton></footer>
      </form>
    </Dialog>
  );
};

interface DocumentActionsPanelProps {
  detail: AdminDocumentPackageDetail;
  companyName: string;
  busy: boolean;
  onPackageCommand: (command: LegalPackageCommand) => Promise<AdminDocumentPackageDetail>;
}

const DocumentActionsPanel: React.FC<DocumentActionsPanelProps> = ({
  detail,
  companyName,
  busy,
  onPackageCommand,
}) => {
  const [pending, setPending] = React.useState<PendingCommand | null>(null);
  const runPackage = (uiCommand: DocumentUiCommand, command: LegalPackageCommand) => {
    if (RequiresConfirmation[uiCommand]) {
      setPending({ uiCommand, packageCommand: command });
    } else {
      void onPackageCommand(command).catch(() => undefined);
    }
  };
  const confirm = async (command: PendingCommand) => {
    if (command.packageCommand) {
      await onPackageCommand(command.packageCommand);
    }
    setPending(null);
  };
  const state = getDocumentStatePresentation(detail.state);
  const generateUi = detail.state === 'generation_failed' ? 'retry_generation' : 'generate';

  return (
    <section className="admin-legal-card" aria-labelledby="package-actions-title">
      <div className="admin-legal-card__heading"><div><span className="admin-legal-eyebrow">Server capabilities only</span><h3 id="package-actions-title">Lifecycle actions</h3></div><HelpTooltipTrigger label="Lifecycle actions" help={superAdminTooltips.legalLifecycleActions} placement="bottom-start" /></div>
      <div className="admin-legal-action-grid">
        <AdminButton disabled={busy || !documentCapabilityAvailable(detail, 'generate')} onClick={() => runPackage(generateUi, 'generate')}>{getDocumentCommandPresentation(generateUi).label}</AdminButton>
        {PackageButtons.map(({ ui, command, capability }) => <AdminButton key={ui} variant={ui === 'void' || ui === 'archive' || ui === 'decline' ? 'danger' : 'default'} disabled={busy || !documentCapabilityAvailable(detail, capability)} onClick={() => runPackage(ui, command)}>{getDocumentCommandPresentation(ui).label}</AdminButton>)}
      </div>
      {!documentCapabilityAvailable(detail, 'send') && <p className="admin-legal-disabled-reason">Send is not available in {state.label}.</p>}
      <TypedCommandDialog pending={pending} companyName={companyName} packageVersion={detail.sequence} busy={busy} onConfirm={confirm} onClose={() => setPending(null)} />
    </section>
  );
};

export default DocumentActionsPanel;
